import { useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Pencil, Trash2 } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Button, Empty, ErrorBlock, Input, Spinner } from "../../components/ui.tsx";
import { orgFetch } from "../../api/org-client.ts";
import { useCategories, useIdentities } from "../../api/queries.ts";
import type { ShopCategory } from "../shop-categories.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 店铺分类
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **「加货架」在网页上常驻右栏，不是一个要先点开的表单。**
 *    摆货架是**连续动作**：加一个瓷砖、再加一个地板、再自己起个「主推」。
 *    手机上只能"点开表单 → 填 → 收起 → 再点开"，网页上有空间就该
 *    让候选品类一直摆在右边，点一个加一个。
 *
 * 🔴 **跟平台的全站品类是两回事，别混。**
 *    平台品类是全站统一的商品学名，改一下影响所有人；
 *    店铺分类是**这家店自己的货架**，只出现在他自己的店里。
 *
 * ⚠️ 排序仍然用上移/下移而不是拖拽 —— 拖拽要处理自动滚动和落点提示，
 *    做不好比不做还糟；两个按钮点了立刻能看出结果。
 */
export function DesktopShopCategories() {
  const search = useSearch({ from: "/shop-categories" }) as { orgId?: number };
  const { data: identities, isPending } = useIdentities();
  const qc = useQueryClient();
  const orgs = identities?.orgs ?? [];
  const orgId = search.orgId ?? orgs[0]?.id;

  const list = useQuery({
    queryKey: ["merchant", orgId, "shop-categories"],
    queryFn: () => orgFetch<ShopCategory[]>("/shop/categories", orgId!),
    enabled: Boolean(orgId),
  });

  const { data: platformCats } = useCategories();

  /**
   * 🔴 **后台改完货架，前台那份缓存也要作废。**
   *    两边是两个查询。只作废后台那份的话，他刚调好顺序、点进自己的店一看
   *    **还是老样子** —— 会以为没保存上，然后反复去调。
   */
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["merchant", orgId, "shop-categories"] });
    void qc.invalidateQueries({ queryKey: ["shop"] });
    void qc.invalidateQueries({ queryKey: ["products"] });
  };

  const [newName, setNewName] = useState("");
  /** 正在改名的那一条。⚠️ 一次只让改一个 —— 同时开几个输入框容易存错。 */
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  const create = useMutation({
    mutationFn: (v: { name: string; platformCategoryId?: number }) =>
      orgFetch("/shop/categories", orgId!, {
        method: "POST",
        body: JSON.stringify({
          name: v.name.trim(),
          sortOrder: list.data?.length ?? 0,
          platformCategoryId: v.platformCategoryId ?? null,
        }),
      }),
    onSuccess: () => {
      setNewName("");
      void invalidate();
    },
  });

  const rename = useMutation({
    mutationFn: (v: { id: number; name: string }) =>
      orgFetch(`/shop/categories/${v.id}`, orgId!, {
        method: "PATCH",
        body: JSON.stringify({ name: v.name.trim() }),
      }),
    onSuccess: () => {
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => orgFetch(`/shop/categories/${id}`, orgId!, { method: "DELETE" }),
    onSuccess: () => {
      setConfirmDel(null);
      void invalidate();
    },
  });

  /**
   * 🔴 **一键按平台品类建货架。**
   *    新店的分类页不该是一张白纸 —— 平台已经有现成的分法。
   */
  const seed = useMutation({
    mutationFn: () =>
      orgFetch<{ created: number }>("/shop/categories/from-platform", orgId!, { method: "POST" }),
    onSuccess: () => invalidate(),
  });

  /**
   * 调顺序。
   * 🔴 **本地先动，再发请求** —— 等服务端回来再刷新的话，
   *    点了要过一会儿才动（像没点上），而且连点两次会基于旧顺序算错。
   */
  const move = useMutation({
    mutationFn: (ids: number[]) =>
      orgFetch("/shop/categories/reorder", orgId!, {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    onMutate: async (ids) => {
      const key = ["merchant", orgId, "shop-categories"];
      /** ⚠️ 先把在飞的请求取消掉，否则它回来会把本地这次改动覆盖回去。 */
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ShopCategory[]>(key);
      if (prev) {
        const byId = new Map(prev.map((c) => [c.id, c]));
        qc.setQueryData(key, ids.map((id) => byId.get(id)).filter(Boolean) as ShopCategory[]);
      }
      return { prev, key };
    },
    /** ⚠️ 失败要把顺序退回去 —— 否则他看到的顺序跟库里的不一样，还不知道。 */
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: () => invalidate(),
  });

  /**
   * 🔴 **没有商家身份时不能只转圈。** disabled 的查询 `isPending` 恒为 true ——
   *    未登录、或名下没有店的人会看到一个永远停不下来的圈。
   */
  if (!isPending && !orgId)
    return (
      <AccountShell title="店铺分类">
        <Empty title="你还不是商家" hint="店铺分类是商家功能。想开店请联系上级商家或平台。" action={{ label: "回首页", to: "/" }} compact />
      </AccountShell>
    );
  if (isPending || list.isPending)
    return (
      <AccountShell title="店铺分类">
        <Spinner compact />
      </AccountShell>
    );
  if (list.error)
    return (
      <AccountShell title="店铺分类">
        <ErrorBlock error={list.error} />
      </AccountShell>
    );

  const items = list.data ?? [];

  const swap = (i: number, j: number) => {
    if (j < 0 || j >= items.length) return;
    const ids = items.map((c) => c.id);
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    move.mutate(ids);
  };

  const unusedPlatform = (platformCats ?? []).filter((c) => !items.some((x) => x.name === c.name));

  return (
    <AccountShell title="店铺分类">
      <p className="mb-5 max-w-[70ch] text-[14px] leading-relaxed text-muted-foreground">
        这是<strong className="text-foreground">你自己店里的货架</strong>
        ，客户进你的店时按这个分组看商品。跟平台的品类（瓷砖 / 地板 / 灯具）是两回事，只影响你的店。
      </p>

      <div className="grid grid-cols-[minmax(0,1fr)_320px] items-start gap-8">
        {/* ══════════ 左：已有的货架 ══════════ */}
        <div>
          {items.length === 0 ? (
            <div className="rounded-xl border border-border bg-card px-6 py-10 text-center">
              <p className="text-[16px] font-medium">还没有分类</p>
              <p className="mx-auto mt-1.5 max-w-[34ch] text-[13px] leading-relaxed text-muted-foreground">
                可以直接用右边的平台品类，也可以自己起名，比如「主推」「工装常用」
              </p>
              <Button className="mt-4" loading={seed.isPending} onClick={() => seed.mutate()}>
                按平台品类一键生成
              </Button>
              {seed.error && <ErrorBlock error={seed.error} />}
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="grid grid-cols-[40px_minmax(0,1fr)_180px_150px] gap-4 bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
                <span>#</span>
                <span>货架名</span>
                <span>收录方式</span>
                <span className="text-right">排序 / 操作</span>
              </div>

              {items.map((c, i) => (
                <div key={c.id} className="border-t border-border px-5 py-3">
                  {editing?.id === c.id ? (
                    <div className="flex items-center gap-3">
                      <Input
                        value={editing.name}
                        onChange={(e) => setEditing({ id: c.id, name: e.target.value })}
                        className="max-w-[280px]"
                      />
                      <Button
                        size="sm"
                        loading={rename.isPending}
                        disabled={!editing.name.trim()}
                        onClick={() => rename.mutate(editing)}
                      >
                        保存
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                        取消
                      </Button>
                      {rename.error && <ErrorBlock error={rename.error} />}
                    </div>
                  ) : confirmDel === c.id ? (
                    <div className="flex items-center gap-3">
                      {/*
                        🔴 说清楚"商品不会跟着没" —— 不说的话他不敢删，
                           或者删了之后以为把货弄丢了。
                      */}
                      <span className="min-w-0 flex-1 text-[13px] text-destructive">
                        删掉「{c.name}」？分类下的 {c.productCount} 件商品不会消失，只是回到未分类。
                      </span>
                      <Button size="sm" variant="outline" onClick={() => setConfirmDel(null)}>
                        取消
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        loading={remove.isPending}
                        onClick={() => remove.mutate(c.id)}
                      >
                        删除
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-[40px_minmax(0,1fr)_180px_150px] items-center gap-4">
                      <span className="text-[13px] text-muted-foreground tabular-nums">
                        {i + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-[15px]">{c.name}</span>
                        <span className="text-[12px] text-muted-foreground">
                          {c.productCount} 件商品
                        </span>
                      </span>
                      {/*
                        ⚠️ 标出来是"自动"还是"手动"：自动货架不用他去归类，
                           不标的话他会白忙一场（去「我的商品」里一件件放）。
                      */}
                      <span className="text-[13px] text-muted-foreground">
                        {c.platformCategoryId ? "按平台品类自动收录" : "手动挑选"}
                      </span>
                      <span className="flex justify-end gap-1">
                        <button
                          className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-30"
                          disabled={i === 0 || move.isPending}
                          onClick={() => swap(i, i - 1)}
                          aria-label="上移"
                        >
                          <ArrowUp className="size-4" />
                        </button>
                        <button
                          className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-30"
                          disabled={i === items.length - 1 || move.isPending}
                          onClick={() => swap(i, i + 1)}
                          aria-label="下移"
                        >
                          <ArrowDown className="size-4" />
                        </button>
                        <button
                          className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                          onClick={() => setEditing({ id: c.id, name: c.name })}
                          aria-label="改名"
                        >
                          <Pencil className="size-[15px]" />
                        </button>
                        <button
                          className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setConfirmDel(c.id)}
                          aria-label="删除"
                        >
                          <Trash2 className="size-[15px]" />
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {move.error && <ErrorBlock error={move.error} />}
        </div>

        {/* ══════════ 右：加货架（常驻） ══════════ */}
        <aside className="sticky top-24 space-y-4 rounded-xl border border-border bg-card p-5">
          <div>
            <p className="text-[15px] font-medium">加货架</p>
            <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
              用平台品类建的货架会<strong className="text-foreground">自动收录</strong>
              店里属于该品类的商品，上游新上的货也会自己进来。
            </p>
          </div>

          {unusedPlatform.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {unusedPlatform.map((c) => (
                <button
                  key={c.id}
                  className="rounded-lg border border-border px-3 py-1.5 text-[13px] transition-colors hover:border-brand-300 hover:text-brand-700"
                  disabled={create.isPending}
                  onClick={() => create.mutate({ name: c.name, platformCategoryId: c.id })}
                >
                  + {c.name}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-muted-foreground">平台品类都已经加过了。</p>
          )}

          <div className="border-t border-border pt-4">
            <Input
              label="或者自己起个名"
              placeholder="主推 / 工装常用 / 样板间同款"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <Button
              className="mt-3 w-full"
              loading={create.isPending}
              disabled={!newName.trim()}
              onClick={() => create.mutate({ name: newName })}
            >
              新建货架
            </Button>
            {create.error && <ErrorBlock error={create.error} />}
          </div>
        </aside>
      </div>
    </AccountShell>
  );
}
